import React, { useEffect, useState } from "react";
import api, { formatApiErrorDetail, formatDate, formatINR } from "@/lib/api";
import { CheckCircle2, ShieldOff, Trash2 } from "lucide-react";
import { toast } from "sonner";
import useAutoRefresh from "@/hooks/useAutoRefresh";
import ConfirmDialog from "@/components/ConfirmDialog";

export default function ClashResolutionTab() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [confirmAction, setConfirmAction] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/donations?clash=true&limit=1000");
      setItems(data || []);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);
  useEffect(() => {
    window.addEventListener("kaya:clashes-updated", load);
    return () => window.removeEventListener("kaya:clashes-updated", load);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useAutoRefresh(load, [], { enabled: !loading, refreshOnFocus: false, intervalMs: 10000 });
  const groups = groupClashedDonations(items);

  const resolve = async (donation, action) => {
    const labels = { clear: "clear clash", verify: "verify" };
    try {
      await api.post(`/admin/donations/${donation.id}/resolve-clash`, { action });
      toast.success(`Clash ${labels[action]} saved`);
      window.dispatchEvent(new Event("kaya:clashes-updated"));
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  const remove = async (donation) => {
    try {
      await api.delete(`/donations/${donation.id}`);
      toast.success("Donation deleted");
      window.dispatchEvent(new Event("kaya:clashes-updated"));
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  const runConfirmAction = async () => {
    const action = confirmAction;
    setConfirmAction(null);
    if (!action) return;
    if (action.action === "delete") await remove(action.donation);
    else await resolve(action.donation, action.action);
  };

  return (
    <div className="space-y-4" data-testid="admin-clash-resolution">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="text-overline">Verification</div>
          <h2 className="text-2xl font-light text-[#2D312E]" style={{ fontFamily: "Manrope" }}>Clash resolution</h2>
          <p className="text-sm text-[#5C635E] mt-1">Resolve duplicate or conflicting donation entries from one place.</p>
        </div>
        <button onClick={load} className="border border-[#EAE6DF] hover:border-[#4A5D4E] rounded-lg px-4 py-2 text-sm text-[#2D312E]">
          Refresh
        </button>
      </div>

      <div className="space-y-4">
        {loading ? (
          <div className="card-warm p-8 text-center text-[#8A938D]">Loading...</div>
        ) : groups.length === 0 ? (
          <div className="card-warm p-8 text-center text-[#8A938D]">No unresolved clashes.</div>
        ) : groups.map((group) => (
          <div key={group.key} className="card-warm overflow-hidden border-l-4 border-l-[#C05746]" data-testid="admin-clash-group">
            <div className="flex items-center justify-between gap-3 flex-wrap px-4 py-3 border-b border-[#EAE6DF] bg-red-50/40">
              <div>
                <div className="font-medium text-[#C05746]">{group.title}</div>
                <div className="text-xs text-[#8A938D]">{group.items.length} clashed donation{group.items.length === 1 ? "" : "s"}</div>
              </div>
              <div className="text-sm font-semibold text-[#4A5D4E]">{formatINR(group.total)}</div>
            </div>
            <ClashRows items={group.items} onAction={(donation, action) => setConfirmAction({ donation, action })} />
          </div>
        ))}
      </div>
      <ConfirmDialog
        open={!!confirmAction}
        title={confirmAction?.action === "delete" ? "Delete donation?" : "Resolve clash?"}
        message={`Are you sure you want to ${confirmAction ? ({ clear: "clear clash", verify: "verify", delete: "delete" }[confirmAction.action]) : ""} for ${confirmAction?.donation?.donor_name || "this donation"}?`}
        confirmLabel={confirmAction?.action === "delete" ? "Delete" : "Confirm"}
        onConfirm={runConfirmAction}
        onCancel={() => setConfirmAction(null)}
      />
    </div>
  );
}

function groupClashedDonations(items) {
  const groups = new Map();
  (items || []).forEach((d) => {
    const date = String(d.donation_date || d.created_at || "").slice(0, 10);
    const key = [d.donor_phone || "", date, Number(d.amount || 0)].join("|");
    if (!groups.has(key)) {
      groups.set(key, {
        key,
        title: `${d.donor_name || "Unknown donor"} | ${d.donor_phone || "No phone"} | ${formatDate(date)} | ${formatINR(d.amount)}`,
        total: 0,
        items: [],
      });
    }
    const group = groups.get(key);
    group.total += Number(d.amount || 0);
    group.items.push(d);
  });
  return Array.from(groups.values()).sort((a, b) => String(a.donor_name || "").localeCompare(String(b.donor_name || "")));
}

function ClashRows({ items, onAction }) {
  return (
    <div className="overflow-x-auto" style={{ WebkitOverflowScrolling: "touch" }}>
      <table className="w-full min-w-[1100px] text-sm">
        <thead>
          <tr className="bg-[#F4F1EA] text-[#5C635E] text-xs uppercase tracking-wider">
            <th className="text-left p-3">Donor</th>
            <th className="text-left p-3">Phone</th>
            <th className="text-left p-3">Employee</th>
            <th className="text-left p-3">Org / Source</th>
            <th className="text-left p-3">Date</th>
            <th className="text-right p-3">Amount</th>
            <th className="text-right p-3">Actions</th>
          </tr>
        </thead>
        <tbody>
          {items.map((d) => (
            <tr key={d.id} data-testid={`clash-row-${d.id}`} className="border-t border-[#EAE6DF]">
              <td className="p-3 font-medium text-[#2D312E]">{d.donor_name}</td>
              <td className="p-3 text-[#5C635E]">{d.donor_phone}</td>
              <td className="p-3 text-[#5C635E]">{d.employee_name}</td>
              <td className="p-3 text-[#5C635E] uppercase">{d.organization || "-"} / {d.source || "-"}</td>
              <td className="p-3 text-[#5C635E]">{formatDate(d.donation_date || d.created_at)}</td>
              <td className="p-3 text-right font-semibold text-[#4A5D4E]">{formatINR(d.amount)}</td>
              <td className="p-3 text-right whitespace-nowrap">
                <button onClick={() => onAction(d, "clear")} className="text-[#4A5D4E] hover:underline mr-3 inline-flex items-center gap-1"><ShieldOff className="h-3.5 w-3.5" />Clear clash</button>
                <button onClick={() => onAction(d, "verify")} className="text-emerald-700 hover:underline mr-3 inline-flex items-center gap-1"><CheckCircle2 className="h-3.5 w-3.5" />Verify</button>
                <button onClick={() => onAction(d, "delete")} className="text-[#C05746] hover:underline inline-flex items-center gap-1"><Trash2 className="h-3.5 w-3.5" />Delete</button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
